import * as THREE from 'three'
import { createPMREMGenerator } from './pmrem.js'

/**
 * Stable cache key derived from the splat URL. First 8 hex chars of a SHA-1
 * digest — filesystem-safe, deterministic, and short enough to be readable
 * in paths and filenames.
 */
export async function splatCacheKey(splatUrl) {
  const bytes = new TextEncoder().encode(splatUrl)
  const hash = await crypto.subtle.digest('SHA-1', bytes)
  return Array.from(new Uint8Array(hash).slice(0, 8))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * HEAD request; resolves true when the server has an actual image at `url`.
 * Must check content-type because Vite's dev-mode SPA fallback serves
 * index.html (text/html) for any unmatched path — `res.ok` alone would
 * misreport non-existent assets as present.
 */
export async function headOk(url) {
  try {
    const res = await fetch(url, { method: 'HEAD' })
    if (!res.ok) return false
    const ct = res.headers.get('content-type') || ''
    return /^image\//.test(ct)
  } catch {
    return false
  }
}

/**
 * Load a single equirectangular (latlong) PNG and prefilter it into an
 * envMap via PMREM. Matches the bake tool's output format.
 */
export function loadEquirectAsEnvMap(gl, url) {
  return new Promise((resolve, reject) => {
    const loader = new THREE.TextureLoader()
    loader.load(
      url,
      async (tex) => {
        tex.mapping = THREE.EquirectangularReflectionMapping
        tex.colorSpace = THREE.SRGBColorSpace
        // Default flipY=true (from TextureLoader) is correct for our bake
        // tool's output: PNG row 0 is the north pole (+Y / sky), which
        // three uploads to GPU V=1 where its equirect sampler expects it.
        const pmrem = await createPMREMGenerator(gl)
        pmrem.compileEquirectangularShader()
        const envRT = pmrem.fromEquirectangular(tex)
        pmrem.dispose()
        tex.dispose()
        resolve(envRT.texture)
      },
      undefined,
      reject,
    )
  })
}
